/*
 * PostHog wrapper utility.
 *
 * Centralizes init / identify / reset / feature-flag access behind a single
 * module so:
 *   1. Token + host are sourced from VITE_POSTHOG_KEY / VITE_POSTHOG_HOST
 *      env vars and never hardcoded.
 *   2. All call sites can `import` from a stable surface that's trivial to
 *      mock with `jest.mock("utils/posthog", ...)` in tests.
 *   3. If the env var is unset (e.g. local dev without an analytics token),
 *      every helper is a safe no-op rather than an init-time crash.
 *
 * Behaviour summary:
 *   - initPostHog(): called once from src/index.js before render. No-op if
 *     VITE_POSTHOG_KEY is unset.
 *   - identify(user): called from App/sagas after the session response loads
 *     a user. Skipped if PostHog never initialized OR if the user has no id.
 *   - reset(): called from the sign-out saga to clear distinct_id, undoing
 *     the identify so subsequent flag evaluation falls back to anonymous.
 *   - isFeatureEnabled(flag): synchronous read of the flag's last cached
 *     decision. Defaults to `false` whenever we can't be certain (SDK not
 *     loaded, key missing, error). The default-off contract is intentional:
 *     callers MUST treat absence as the legacy / safe path.
 *   - capture(event, properties): explicit product-usage event. Never throws;
 *     no-ops (after bounded pre-init buffering) when the SDK never
 *     initializes, e.g. keyless local dev. Call sites MUST send identifiers
 *     only (ids, slugs) — never emails, phone numbers, or photo content.
 */

import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { env } from "utils/env";

const DEFAULT_API_HOST = "https://us.posthog.com";

let initialized = false;

export const isInitialized = () => initialized;

/*
 * Pre-init buffering.
 *
 * Init is no longer guaranteed to happen before the rest of the app runs: on a
 * public gallery route it is deliberately skipped (see utils/thirdParty), and
 * an authenticated user can land there first — a shared photo link — before
 * navigating into the dashboard. Meanwhile `containers/App/sagas` restores the
 * session and calls identify(), and `containers/App` subscribes the global flag
 * hook. Both are ONE-SHOT: getSessionFlow is a boot fork that never re-runs, and
 * useFeatureFlag's effect deps never change. Dropping either call strands that
 * user anonymous, or their flags stuck on the default, for the whole session.
 *
 * So both are buffered and replayed at init. Bounds, because a guest may sit on
 * the gallery forever and init may never come:
 *   - Identity is ONE slot, last-write-wins (identity is not a stream), so it
 *     cannot grow and cannot double-identify on replay.
 *   - Subscribers are keyed by a record the caller can release; unmounting
 *     removes it, so the set tracks live components, not history.
 *   - Captures are a BOUNDED queue (PENDING_CAPTURE_LIMIT, drop-newest).
 *     Guest routes now init PostHog deferred past load+idle (see
 *     utils/thirdParty), so a `gallery_viewed` fired at mount predates init by
 *     design and must not be lost. The bound matters because a keyless
 *     environment never inits, so the queue must not grow with a guest who
 *     browses forever; a handful of early events is all the deferral window
 *     can legitimately produce.
 */
let pendingIdentify = null;
const pendingFlagSubscribers = new Set();

const PENDING_CAPTURE_LIMIT = 20;
let pendingCaptures = [];

const flushPending = () => {
  // Subscribers first: identify() triggers a fresh flag decision, and attaching
  // beforehand guarantees a pre-init subscriber sees the identified result
  // rather than only the anonymous one.
  const subscribers = Array.from(pendingFlagSubscribers);
  pendingFlagSubscribers.clear();
  subscribers.forEach((record) => {
    if (record.released) return;
    record.unsubscribe = posthog.onFeatureFlags(record.callback);
  });

  if (pendingIdentify) {
    const user = pendingIdentify;
    // The single slot is what makes the replay exactly-once: identify() below
    // now takes the initialized path, and nothing re-reads the slot (initPostHog
    // early-returns once initialized). Clearing it is belt-and-braces against a
    // future re-init path rather than a live guard — no current test can
    // distinguish leaving it set.
    pendingIdentify = null;
    identify(user);
  }

  // Captures last: replaying after identify() attributes a signed-in user's
  // pre-init events to their identified distinct_id rather than splitting
  // them across an anonymous id.
  const captures = pendingCaptures;
  pendingCaptures = [];
  captures.forEach(([event, properties]) => capture(event, properties));
};

/*
 * before_send filter: drop any event captured while the user is inside the
 * gated superadmin section (routes.js mounts it at /admin). Filtering at the
 * SDK boundary catches $pageview/$pageleave as well as any explicit capture a
 * future component might add, so no per-call-site discipline is required.
 * Exported for tests only.
 */
export const dropAdminRouteEvents = (event) => {
  if (!event) return event;
  const url = event.properties?.$current_url;
  if (typeof url === "string") {
    try {
      if (new URL(url).pathname.startsWith("/admin")) return null;
    } catch {
      // Unparseable URL — keep the event rather than guessing.
    }
  }
  return event;
};

/*
 * A shared photo's link carries its access key (`?k=`, see MoveView). It is a
 * credential to that photo, so it must not reach analytics: strip it from every
 * URL-valued property ($current_url, $referrer, the $set_once initial URLs...)
 * before the event leaves the browser. /start carries two more: `email` (the
 * prefilled address, PII) and `resume` (the 4A resume token, a credential).
 * Exported for tests only.
 */
const SECRET_PARAMS = ["k", "email", "resume"];

const scrubUrl = (value) => {
  if (typeof value !== "string" || !/^https?:\/\//.test(value)) return value;
  try {
    const url = new URL(value);
    if (!SECRET_PARAMS.some((param) => url.searchParams.has(param))) return value;
    SECRET_PARAMS.forEach((param) => url.searchParams.delete(param));
    return url.toString();
  } catch {
    // Unparseable, so the key can't be located: fail closed and drop the whole
    // query and fragment rather than let a possible credential reach analytics.
    return value.replace(/[?#].*$/, "");
  }
};

const scrubProps = (props) => {
  if (!props || typeof props !== "object") return props;
  return Object.fromEntries(Object.entries(props).map(([name, value]) => [name, scrubUrl(value)]));
};

/*
 * Defence in depth: no PII reaches PostHog. Call sites already send ids only;
 * this drops any key that names contact details or a person's name, at the SDK
 * boundary, in event properties and in $set / $set_once. Exported for tests.
 */
const PII_KEY =
  /(^|[_.-])(e-?mail|phone|mobile|tel|first_?name|last_?name|full_?name|name|dob|birth|birthday|address|ssn)($|[_.-])|^\$(email|name)$/i;

export const stripPii = (props) => {
  if (!props || typeof props !== "object") return props;
  return Object.fromEntries(Object.entries(props).filter(([name]) => !PII_KEY.test(name)));
};

export const stripShareKeys = (event) => {
  if (!event) return event;
  return {
    ...event,
    properties: scrubProps(event.properties),
    ...(event.$set ? { $set: scrubProps(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: scrubProps(event.$set_once) } : {}),
  };
};

const stripEventPii = (event) => {
  if (!event) return event;
  return {
    ...event,
    properties: stripPii(event.properties),
    ...(event.$set ? { $set: stripPii(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: stripPii(event.$set_once) } : {}),
  };
};

export const beforeSend = (event) => stripEventPii(stripShareKeys(dropAdminRouteEvents(event)));

export const initPostHog = () => {
  if (initialized) return;
  const key = env.VITE_POSTHOG_KEY;
  if (!key) return;
  const apiHost = env.VITE_POSTHOG_HOST || DEFAULT_API_HOST;
  posthog.init(key, {
    api_host: apiHost,
    // SPA pageviews: the SDK patches the History API and captures a $pageview
    // on init plus one per route change — the posthog-js-native alternative
    // to a hand-rolled react-router listener. Supported by the pinned
    // posthog-js version (1.372.x).
    capture_pageview: "history_change",
    // Avoid surprise event-collection on every click/input. Explicit capture
    // calls only.
    autocapture: false,
    // Anonymous visitors (event guests on the public gallery) stay
    // profile-less anonymous events; only identify() creates a person.
    person_profiles: "identified_only",
    persistence: "localStorage",
    // The superadmin section is operator tooling, not product usage, and its
    // URLs can reference other users' resources. Nothing captured there —
    // pageviews included — leaves the browser. Share-link keys are stripped
    // from every URL that does.
    before_send: beforeSend,
  });
  initialized = true;
  flushPending();
};

export const identify = (user) => {
  if (!user || user.id == null) return;
  if (!initialized) {
    // Buffered, not dropped — see the pre-init buffering note above. One slot:
    // a later identify supersedes an earlier one, exactly as it would post-init.
    pendingIdentify = user;
    return;
  }
  const distinctId = String(user.id);
  // Distinct id only: no person properties, so no email or other PII.
  posthog.identify(distinctId);
};

/*
 * Explicit product-usage event. The contract call sites rely on:
 *   - NEVER throws — a gallery guest's page must not break because analytics
 *     is unavailable, blocked, or keyless.
 *   - Pre-init calls are buffered (bounded, drop-newest) and replayed at
 *     init — see the pre-init buffering note above.
 *   - Properties are identifiers only (ids, slugs, method names). No emails,
 *     phone numbers, free-text, or photo/gif content.
 */
export const capture = (event, properties) => {
  if (!event) return;
  if (!initialized) {
    if (pendingCaptures.length < PENDING_CAPTURE_LIMIT) {
      pendingCaptures.push([event, properties]);
    }
    return;
  }
  try {
    posthog.capture(event, stripPii(properties));
  } catch {
    // Analytics must never take the product down with it.
  }
};

export const reset = () => {
  if (!initialized) {
    // Sign-out before init (possible on a public route, where init is skipped).
    // Drop the buffered identity: replaying it after a sign-out would re-bind a
    // signed-out user's events to their old distinct_id — the exact leak
    // reset() exists to prevent.
    pendingIdentify = null;
    return;
  }
  posthog.reset();
};

/*
 * Two flag-override mechanisms, both intended for CI / E2E / local debug
 * use only — production deployments should rely on PostHog's rollout to
 * make the decision.
 *
 *   1. Build-time env var: VITE_FF_<UPPER_SNAKE_CASE_FLAG_KEY>=true
 *      Compiled into the bundle. Useful for an E2E build that always
 *      exercises the flag-on path.
 *
 *   2. Runtime localStorage: ff:<flag_key> = "true"
 *      Settable by Playwright tests via page.addInitScript or by humans
 *      in DevTools. Bundle-independent, so the same prod build can be
 *      driven into either branch in the field.
 *
 * Both default-off if absent — the legacy / safe path stays the default.
 *
 * Static flag→env map. Vite statically replaces only LITERAL
 * `import.meta.env.VITE_X` member accesses at build time; a computed key
 * (`import.meta.env[`VITE_FF_${...}`]`) cannot be resolved and would silently
 * read undefined. So each supported flag gets one explicit entry here, keyed by
 * the PostHog flag key, whose getter reads the one literal `VITE_FF_*` accessor.
 * Reading via a getter (rather than capturing the value at module-load) keeps
 * the lookup live: under Vite the value is a build-time constant so it's stable,
 * but a getter also lets tests vary the mocked env between cases. To add a
 * future flag override:
 *   1. Add `"<posthog-flag-key>": () => env.VITE_FF_<UPPER_SNAKE_CASE_KEY>`.
 *   2. Document the var in .env.example and (if used in CI) ci.yml.
 * The env-var POSIX name is the flag key uppercased with `-`→`_`, so the
 * PostHog flag "my-feature-flag" → VITE_FF_MY_FEATURE_FLAG.
 */
const FLAG_ENV_OVERRIDES = {
  multi_select_picker_enabled: () => env.VITE_FF_MULTI_SELECT_PICKER_ENABLED,
  react_superadmin_enabled: () => env.VITE_FF_REACT_SUPERADMIN_ENABLED,
  ai_capture_mode_enabled: () => env.VITE_FF_AI_CAPTURE_MODE_ENABLED,
  story_video_enabled: () => env.VITE_FF_STORY_VIDEO_ENABLED,
};

const buildTimeOverride = (flagKey) => {
  const read = FLAG_ENV_OVERRIDES[flagKey];
  return read ? read() === "true" : false;
};

const runtimeOverride = (flagKey) => {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(`ff:${flagKey}`) === "true";
  } catch {
    // Some browser configs (private mode on iOS, sandboxed iframes) throw
    // SecurityError on localStorage access. Treat as "no override".
    return false;
  }
};

export const isFeatureEnabled = (flagKey) => {
  if (buildTimeOverride(flagKey)) return true;
  if (runtimeOverride(flagKey)) return true;
  if (!initialized) return false;
  // posthog.isFeatureEnabled returns true | false | undefined (undefined when
  // the flag definition hasn't been fetched yet). Coerce to a strict boolean
  // so the caller's ternary always picks a deterministic branch — the
  // default-off branch is the safe one when the SDK hasn't decided yet.
  return posthog.isFeatureEnabled(flagKey) === true;
};

/*
 * Subscribe to flag-decision changes. Returns an unsubscribe function, or a
 * no-op when the SDK is not initialized so callers don't have to branch on
 * the return value. Used by useFeatureFlag below to re-render when the
 * initial flag fetch resolves or when the user is re-identified.
 */
export const onFeatureFlags = (callback) => {
  if (initialized) return posthog.onFeatureFlags(callback);

  // Pre-init: hold the callback and attach it to the SDK at init. The returned
  // unsubscribe works either side of that handover — before the flush it just
  // drops the record (so a component that unmounts on the gallery retains
  // nothing), after it forwards to the SDK's own unsubscribe.
  const record = { callback, unsubscribe: null, released: false };
  pendingFlagSubscribers.add(record);
  return () => {
    record.released = true;
    pendingFlagSubscribers.delete(record);
    if (record.unsubscribe) record.unsubscribe();
  };
};

/*
 * React hook that returns the current value of a feature flag, re-rendering
 * when posthog publishes a new decision (e.g. on initial flag fetch, after
 * identify(), or on a manual reload). Defaults to `false` until the SDK has
 * a definitive answer, so the caller's render-time branch is always the
 * legacy / safe path until proven otherwise.
 *
 * We deliberately don't use posthog-js/react's `useFeatureFlagEnabled` hook
 * because that requires wrapping the React tree in <PostHogProvider>; we
 * already initialise via the bare posthog-js singleton in src/index.js so
 * adding the provider would either duplicate init or require restructuring
 * the app shell. The shape returned here matches PostHog's hook (boolean) so
 * callers read identically.
 */
export const useFeatureFlag = (flagKey) => {
  const [enabled, setEnabled] = useState(() => isFeatureEnabled(flagKey));
  useEffect(() => {
    // Re-read in case the flag value changed between render and effect mount
    // (e.g. flags resolved synchronously after the initial useState call).
    setEnabled(isFeatureEnabled(flagKey));
    const unsubscribe = onFeatureFlags(() => {
      setEnabled(isFeatureEnabled(flagKey));
    });
    return unsubscribe;
  }, [flagKey]);
  return enabled;
};

// Exported only so tests can reset module state between cases. Production
// code should not call this.
export const __resetForTests = () => {
  initialized = false;
  pendingIdentify = null;
  pendingFlagSubscribers.clear();
  pendingCaptures = [];
};
